import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BaseNote, EncounterOpenCountDetails, EncounterState, FullNote, UpdateEncounterProcessingStatusPayload } from "./index.types";

const name = "Encounter";

export const initialEncounterState: EncounterState = {
  openEncountersCount: 0,
  encounter: {},
  encounterSuccess: null,
  encounterByAppointmentSuccess: null,
  encounterMessage: "",
  isEncounterLoading: false,
  isEncounterCreateOrUpdateLoading: false,
  isEncounterByIdLoading: false,
  isCloseEncounterPending: false,
  encounterCloseSuccess: null,
  intentToCloseEncounter: false,
  saveEncounterNoteLoading: false,
  saveEncounterNoteSuccess: null,
  isEncounterByAppointmentLoading: false,
  encounterStep: null,
  startEncDate: null,
  endVisitFlag: false,
};

export const EncounterSlice = createSlice({
  name,
  initialState: initialEncounterState,
  reducers: {
    setEncounterOpenCountDetails: (state, { payload }: PayloadAction<EncounterOpenCountDetails>) => {
      state.openEncountersCount = payload.count;
      state.openEncountersProcessing = payload.isLoading;
    },
    setStartEncDate: (state, { payload }: PayloadAction<string | null>) => {
      state.startEncDate = payload;
    },
    setEndVisitFlag: (state, { payload }: PayloadAction<boolean>) => {
      state.endVisitFlag = payload;
    },
    encounter: (state) => {
      state.isEncounterLoading = true;
      state.isEncounterCreateOrUpdateLoading = true;
    },
    closeEncounter: (state) => {
      state.isCloseEncounterPending = true;
    },
    saveEncounterNote: (state) => {
      state.saveEncounterNoteLoading = true;
    },
    closeEncounterSuccess: (state) => {
      state.isCloseEncounterPending = false;
      state.encounterCloseSuccess = true;
    },
    closeEncounterFail: (state) => {
      state.isCloseEncounterPending = false;
      state.encounterCloseSuccess = false;
    },
    saveEncounterNoteSuccess: (state) => {
      state.saveEncounterNoteLoading = false;
      state.saveEncounterNoteSuccess = true;
    },
    saveEncounterNoteFail: (state) => {
      state.saveEncounterNoteLoading = false;
      state.saveEncounterNoteSuccess = false;
    },
    encounterByAppointment: (state) => {
      state.isEncounterByAppointmentLoading = true;
    },
    encounterSuccess: (state, action: PayloadAction<FullNote>) => {
      state.encounter = action.payload;
      state.encounterSuccess = true;
      state.isEncounterLoading = false;
      state.isEncounterCreateOrUpdateLoading = false;
      state.encounterMessage = "Encounter created successfully";
    },
    setEncounterStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.intentToCloseEncounter = payload;
    },
    encounterByAppointmentSuccess: (state, action: PayloadAction<FullNote>) => {
      state.isEncounterByAppointmentLoading = false;
      state.encounterByAppointmentSuccess = true;
      state.encounter = action.payload;
    },
    encounterById: (state) => {
      state.isEncounterLoading = true;
      state.isEncounterByIdLoading = true;
    },
    encounterByIdSuccess: (state, action: PayloadAction<FullNote>) => {
      state.encounter = action.payload;
      state.isEncounterLoading = false;
      state.isEncounterByIdLoading = false;
    },
    encounterByIdFail: (state) => {
      state.isEncounterLoading = false;
      state.isEncounterByIdLoading = false;
    },
    encounterFail: (state) => {
      state.encounterSuccess = false;
      state.isEncounterLoading = false;
      state.isEncounterCreateOrUpdateLoading = false;
      state.encounterMessage = "Unable to create encounter";
    },
    encounterByAppointmentFail: (state) => {
      state.isEncounterByAppointmentLoading = false;
      state.encounterByAppointmentSuccess = false;
      state.encounterByAppointmentData = {};
    },
    clearEncounterData: (state) => {
      state.encounter = {};
      state.encounterMessage = "";
      state.encounterSuccess = null;
      state.isEncounterLoading = false;
      state.isEncounterCreateOrUpdateLoading = false;
      state.encounterStep = null;
    },
    clearAllEncounterData: (state) => {
      state.encounter = {};
      state.encounterSuccess = null;
      state.encounterByAppointmentSuccess = null;
      state.encounterMessage = "";
      state.isEncounterLoading = false;
      state.isEncounterCreateOrUpdateLoading = false;
      state.isEncounterByIdLoading = false;
      state.isCloseEncounterPending = false;
      state.encounterCloseSuccess = null;
      state.intentToCloseEncounter = false;
      state.isEncounterByAppointmentLoading = false;
      state.encounterStep = null;
    },
    clearEncounterByPatientData: (state) => {
      state.encounterByPatientData = [];
    },
    clearCloseEncounterData: (state) => {
      state.isCloseEncounterPending = false;
      state.encounterCloseSuccess = null;
    },
    clearSaveEncounterNoteData: (state) => {
      state.saveEncounterNoteLoading = false;
      state.saveEncounterNoteSuccess = null;
    },
    setEncounterStep: (state, { payload }: PayloadAction<string | number | null>) => {
      state.encounterStep = payload;
    },
    updateEncounterProcessingStatus: (state, { payload }: PayloadAction<UpdateEncounterProcessingStatusPayload>) => {
      if (state.encounter && (state.encounter as FullNote).id === payload.encounterRecordingId) {
        (state.encounter as FullNote).processing_status = payload.status;
      }
    },
  },
});

export const {
  encounter,
  clearEncounterData,
  clearAllEncounterData,
  setEncounterOpenCountDetails,
  clearEncounterByPatientData,
  clearCloseEncounterData,
  setEncounterStatus,
  clearSaveEncounterNoteData,
  setEncounterStep,
  setStartEncDate,
  setEndVisitFlag,
} = EncounterSlice.actions;

export default EncounterSlice.reducer;

export const createOrUpdateEncounter = (data: BaseNote, method: string, encounterId: string | number) => ({
  payload: {
    apiName: name,
    data,
    method: method,
    formData: false,
    url: method !== "POST" ? `/api/encounter-recordings/${encounterId}/` : `/api/encounter-recordings/`,
  },
  type: "encounter",
});

export const closeEncounter = (encounterId: string | number, data: BaseNote) => ({
  payload: {
    apiName: name,
    method: "post",
    data,
    url: `/api/encounter-recordings/${encounterId}/close/`,
  },
  type: "closeEncounter",
});

export const saveEncounterNote = (encounterId: string | number) => ({
  payload: {
    apiName: name,
    method: "post",
    url: `/api/encounter-recordings/${encounterId}/validate-emr-encounter-chart/`,
  },
  type: "saveEncounterNote",
});

export const getEncounterByAppointment = (appointmentId: string | number) => ({
  payload: {
    apiName: name,
    method: "get",
    url: `/api/encounter-recordings/search_by_appointment_id/?appointment_id=${appointmentId}`,
  },
  type: "encounterByAppointment",
});

export const getEncounterById = (encounterId: string | number) => ({
  payload: {
    apiName: name,
    method: "get",
    url: `/api/encounter-recordings/${encounterId}/`,
  },
  type: "encounterById",
});
